import api, { deduplicatedApi } from './index';

const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes
const CACHE_KEY_PREFIX = 'documents_';

// Cache helper
const getFromCache = (key) => {
  const cached = localStorage.getItem(key);
  if (!cached) return null;
  
  const { data, timestamp } = JSON.parse(cached);
  const age = Date.now() - timestamp;
  
  if (age > CACHE_DURATION) {
    localStorage.removeItem(key);
    return null;
  }
  
  return { data, isStale: age > (CACHE_DURATION * 0.8) };
};

const saveToCache = (key, data) => {
  const cacheData = {
    data,
    timestamp: Date.now()
  };
  localStorage.setItem(key, JSON.stringify(cacheData));
};

const invalidateCache = (pattern = null) => {
  if (pattern) {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(CACHE_KEY_PREFIX) && key.includes(pattern)) {
        localStorage.removeItem(key);
        console.log(`Invalidated cache key: ${key}`);
      }
    });
  } else {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(CACHE_KEY_PREFIX)) {
        localStorage.removeItem(key);
        console.log(`Invalidated cache key: ${key}`);
      }
    });
  }
};

/**
 * Get all documents with optional filters
 */
export const getDocuments = async (params = {}, useCache = true) => {
  const cacheKey = `${CACHE_KEY_PREFIX}list_${JSON.stringify(params)}`;
  
  if (useCache) {
    const cached = getFromCache(cacheKey);
    if (cached) {
      // Return cached data immediately
      if (!cached.isStale) {
        return cached.data;
      }
      // If stale, fetch in background and return cached
      deduplicatedApi.get('/document-management/documents/', { params })
        .then(response => {
          saveToCache(cacheKey, response.data);
        })
        .catch(err => console.error('Background refresh failed:', err));
      
      return cached.data;
    }
  }
  
  const response = await deduplicatedApi.get('/document-management/documents/', { params });
  saveToCache(cacheKey, response.data);
  return response.data;
};

/**
 * Get a single document by ID
 */
export const getDocument = async (id) => {
  const cacheKey = `${CACHE_KEY_PREFIX}detail_${id}`;
  const cached = getFromCache(cacheKey);
  
  if (cached && !cached.isStale) {
    return cached.data;
  }
  
  const response = await deduplicatedApi.get(`/document-management/documents/${id}/`);
  saveToCache(cacheKey, response.data);
  return response.data;
};

/**
 * Upload a new document
 */
export const uploadDocument = async (formData) => {
  const response = await api.post('/document-management/documents/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  
  // Invalidate ALL document caches to ensure fresh data
  // This will clear list, statistics, folders, categories, and detail caches
  invalidateCache(); // No pattern = invalidate all document caches
  
  return response.data;
};

/**
 * Update document metadata
 */
export const updateDocument = async (id, data) => {
  const response = await api.patch(`/document-management/documents/${id}/`, data);
  
  // Invalidate caches
  invalidateCache(`detail_${id}`);
  invalidateCache('list');
  
  return response.data;
};

/**
 * Delete a document
 */
export const deleteDocument = async (id) => {
  const response = await api.delete(`/document-management/documents/${id}/`);
  
  // Invalidate all document caches to ensure fresh data
  invalidateCache(); // No pattern = invalidate all document caches
  
  return response.data;
};

/**
 * Download a document
 */
export const downloadDocument = async (id) => {
  const response = await api.get(`/document-management/documents/${id}/download/`, {
    responseType: 'blob'
  });
  return response.data;
};

/**
 * Share a document with users
 */
export const shareDocument = async (id, data) => {
  const response = await api.post(`/document-management/documents/${id}/share/`, data);
  
  // Invalidate cache
  invalidateCache(`detail_${id}`);
  
  return response.data;
};

/**
 * Unshare a document from users
 */
export const unshareDocument = async (id, data) => {
  const response = await api.post(`/document-management/documents/${id}/unshare/`, data);
  
  // Invalidate cache
  invalidateCache(`detail_${id}`);
  
  return response.data;
};

/**
 * Create a new version of a document
 */
export const createDocumentVersion = async (id, formData) => {
  const response = await api.post(`/document-management/documents/${id}/create_version/`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  
  // Invalidate caches
  invalidateCache('list');
  
  return response.data;
};

/**
 * Get document statistics
 */
export const getDocumentStatistics = async () => {
  const cacheKey = `${CACHE_KEY_PREFIX}statistics`;
  const cached = getFromCache(cacheKey);
  
  if (cached && !cached.isStale) {
    return cached.data;
  }
  
  const response = await deduplicatedApi.get('/document-management/documents/statistics/');
  saveToCache(cacheKey, response.data);
  return response.data;
};

/**
 * Get document categories
 */
export const getDocumentCategories = async () => {
  const cacheKey = `${CACHE_KEY_PREFIX}categories`;
  const cached = getFromCache(cacheKey);
  
  if (cached) {
    return cached.data;
  }
  
  const response = await deduplicatedApi.get('/document-management/categories/');
  saveToCache(cacheKey, response.data);
  return response.data;
};

/**
 * Get all folders
 */
export const getFolders = async (params = {}) => {
  const cacheKey = `${CACHE_KEY_PREFIX}folders_${JSON.stringify(params)}`;
  const cached = getFromCache(cacheKey);
  
  if (cached && !cached.isStale) {
    return cached.data;
  }
  
  const response = await deduplicatedApi.get('/document-management/folders/', { params });
  saveToCache(cacheKey, response.data);
  return response.data;
};

/**
 * Create a new folder
 */
export const createFolder = async (data) => {
  const response = await api.post('/document-management/folders/', data);
  
  // Invalidate all document caches since folder structure changed
  invalidateCache(); // No pattern = invalidate all document caches
  
  return response.data;
};

/**
 * Update a folder
 */
export const updateFolder = async (id, data) => {
  const response = await api.patch(`/document-management/folders/${id}/`, data);
  
  // Invalidate folder cache
  invalidateCache('folders');
  
  return response.data;
};

/**
 * Delete a folder
 */
export const deleteFolder = async (id) => {
  const response = await api.delete(`/document-management/folders/${id}/`);
  
  // Invalidate caches
  invalidateCache('folders');
  invalidateCache('list');
  
  return response.data;
};

/**
 * Download shared document (no auth required)
 */
export const downloadSharedDocument = async (shareLink) => {
  const response = await api.get(`/document-management/shared/${shareLink}/`, {
    responseType: 'blob'
  });
  return response.data;
};

// Export cache invalidation for external use
export const invalidateDocumentCache = invalidateCache;

